import { Text } from 'preact-i18n';
import isDateBlocked from '../utils/isDateBlocked';
import NoTripsIcon from './NoTripsIcon';

/**
 * Custom DayButton for react-day-picker. When `badgeText` is set it overlays a
 * small label on the selected day for outbound/return in round-trip mode when
 * one leg is an open ticket.
 * When `showTodayAccentHint` is true, today's cell uses accent colour + icon
 * (unless today is the outbound or return endpoint of the selected range).
 * In availability mode every blocked day shows the crossed-hash "no trips" icon
 * in place of the price-level dot. For a blocked day the hash is an in-flow
 * child and the button becomes a centered flex column (`udp-day_button--has-hash`)
 * so the number and hash read as one vertical group.
 */
const DayButtonWithBadge = ({
  modifiers,
  children,
  badgeText,
  showTodayAccentHint,
  dayData,
  useAvailability,
  // eslint-disable-next-line no-unused-vars
  day,
  onDayWithPriceHover,
  className,
  ...buttonProps
}) => {
  const isTodayAccentHint =
    showTodayAccentHint && modifiers.today && !modifiers.outside;

  const isRangeEndpoint =
    modifiers.range_start ||
    modifiers.range_end ||
    (modifiers.selected && !modifiers.range_middle);

  // Settled-unavailable day. Pending days stay selectable, so they are not
  // treated as blocked here.
  const isBlocked = isDateBlocked(dayData);

  // In availability mode the crossed-hash marks every blocked day, not only
  // selected endpoints. Outside availability mode there is no day data, so no
  // marker is shown.
  const showCrossedHash = Boolean(useAvailability) && isBlocked;

  const priceLevelIndicatorClass =
    isRangeEndpoint && dayData?.available && dayData?.priceLevel
      ? `udp-day_button--price-dot udp-day_button--price-dot-${dayData.priceLevel}`
      : '';

  const mergedClassName = [
    className,
    isTodayAccentHint ? 'udp-day_button--today-hint' : '',
    priceLevelIndicatorClass,
    showCrossedHash ? 'udp-day_button--has-hash' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleMouseEnter = e => {
    buttonProps.onMouseEnter?.(e);
    if (dayData?.minPrice != null && onDayWithPriceHover) {
      onDayWithPriceHover(dayData);
    }
  };

  return (
    <button {...buttonProps} className={mergedClassName} onMouseEnter={handleMouseEnter}>
      <span className="udp-day_button__num">{children}</span>
      {showCrossedHash && (
        <span className="udp-day_button--unavailable-hash">
          <NoTripsIcon />
          <span className="udp-sr-only">
            <Text id="label.price_level_no_trips" />
          </span>
        </span>
      )}
      {modifiers.selected && badgeText && <span className="udp-day-badge">{badgeText}</span>}
    </button>
  );
};

export default DayButtonWithBadge;
